/**
 * <brand-countdown> custom element
 *
 * Supports three modes for the countdown target, in priority order:
 *   1. target="YYYY-MM-DDTHH:MM(:SS)?"     → counts down to this absolute time
 *      (parsed as visitor-local time when no timezone suffix is present).
 *   2. data-hours-from-now="N"             → counts down N hours from page load.
 *   3. data-end-of-day="true"              → counts down to 23:59:59 of the
 *      current day in the visitor's own timezone. Resets daily.
 *
 * On expiry the inner `.brand-countdown` wrapper is hidden and the element
 * with [data-expired] is shown.
 */
if (!customElements.get('brand-countdown')) {
  class BrandCountdown extends HTMLElement {
    constructor() {
      super();
      this.target = this._resolveTarget();
      this.timerEl = this.querySelector('.brand-countdown');
      this.daysEl = this.querySelector('[data-days]');
      this.hoursEl = this.querySelector('[data-hours]');
      this.minutesEl = this.querySelector('[data-minutes]');
      this.secondsEl = this.querySelector('[data-seconds]');
      this.expiredEl = this.querySelector('[data-expired]');
    }

    _resolveTarget() {
      const raw = this.getAttribute('target') || '';
      if (raw) {
        // Mode 1: explicit ISO. Normalize to handle missing seconds.
        const match = raw.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/);
        let iso = raw;
        if (match) {
          iso = `${match[1]}T${match[2]}:${match[3]}:${match[4] || '00'}`;
        }
        const t = new Date(iso).getTime();
        if (!Number.isNaN(t)) return t;
      }

      const hoursFromNow = parseInt(this.getAttribute('data-hours-from-now') || '', 10);
      if (!Number.isNaN(hoursFromNow) && hoursFromNow > 0) {
        // Mode 2: N hours from page view.
        return Date.now() + hoursFromNow * 3600 * 1000;
      }

      if (this.getAttribute('data-end-of-day') === 'true') {
        // Mode 3: end of today in visitor-local time.
        const now = new Date();
        const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        return end.getTime();
      }

      return NaN;
    }

    connectedCallback() {
      if (Number.isNaN(this.target)) return;
      this.tick();
      this.interval = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      if (this.interval) clearInterval(this.interval);
    }

    tick() {
      const diff = this.target - Date.now();
      if (diff <= 0) {
        clearInterval(this.interval);
        if (this.timerEl) this.timerEl.hidden = true;
        if (this.expiredEl) this.expiredEl.hidden = false;
        return;
      }
      const days = Math.floor(diff / 86400000);
      const hours = Math.floor((diff % 86400000) / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      if (this.daysEl) this.daysEl.textContent = String(days).padStart(2, '0');
      if (this.hoursEl) this.hoursEl.textContent = String(hours).padStart(2, '0');
      if (this.minutesEl) this.minutesEl.textContent = String(minutes).padStart(2, '0');
      if (this.secondsEl) this.secondsEl.textContent = String(seconds).padStart(2, '0');
    }
  }
  customElements.define('brand-countdown', BrandCountdown);
}
